import Menu from "../Menu.js";

// scene imports
import Placeholder from "../scene/Placeholder.js";
import Trace       from "../scene/Trace.js";

import global from "../global.js";

// global imports
// const log = global.log.getLogger("f_mtac.ui.VoronoiArea");
const d3  = global.d3;

// drivers
import Line        from "../scene/drivers/Line.js";
import Disk        from "../scene/drivers/Disk.js";
import VoronoiPole from "../scene/drivers/VoronoiPole.js";

const voronoi_drivers = {
    "voronoi_line": Line,
    "voronoi_disk": Disk
};

const VoronoiArea = function() {
    var configuration = null;
    this.init = function() {
        const scene = global.scene;
        const tools = global.tools;

        const voronoi_colors = [
            {name: "color_1", value: "#5d50a3", intensity: 50},
            {name: "color_2", value: "#85cda5", intensity: 80},
            {name: "color_3", value: "#faf8b0", intensity: 130},
            {name: "color_4", value: "#f47d4d", intensity: 200},
            {name: "color_5", value: "#aa0f45", intensity: 255}
        ];

        const build_data = {
            placeholder: null,
            active_object: null,
            active_driver: null,
            active_color: null
        };

        const driver_load = (name) => {
            if (!(name in voronoi_drivers)) {
                throw `voronoi driver ${name} doesn't exist`;
            }
            build_data.active_driver = voronoi_drivers[name];
        };

        const driver_unload = () => {
            build_data.active_driver = null;
        };

        const color_load = (color) =>{
            build_data.active_color = color;
        };

        const color_unload = () =>{
            build_data.active_color = null;
        };

        const voronoi_placeholder = new Placeholder(scene.objects.layers.scene.group, "graphics", {
            start: (data, placeholder) => {
                placeholder.move({x: d3.event.dx, y: d3.event.dy});
                if (build_data.active_driver === null || build_data.active_color === null) {
                    return;
                }
                let {x, y} = placeholder.indicator_position;
                data.active_object = new build_data.active_driver(scene.objects.layers.scene.group, build_data.active_color.value);
                data.active_object.draw(x, y);
            },
            drag: (data, placeholder) => {
                Menu.unset_close_timeout(["voronoi_tools_area"]);
                placeholder.move({x: d3.event.dx, y: d3.event.dy});
                if (build_data.active_driver === null || build_data.active_color === null) {
                    return;
                }
                let {x, y} = placeholder.indicator_position;
                data.active_object.update(x, y);
            },
            end: (data, placeholder) => {
                if (build_data.active_driver === null || build_data.active_color === null) {
                    return;
                }
                var points = [];
                if (data.active_object.data.radius) {
                    let step = 50;
                    for (let r = data.active_object.data.radius; r > step; r -= step) {
                        data.active_object.update_radius(r);
                        points = points.concat(data.active_object.points(step));
                    }
                    points.push({x: data.active_object.data.center.x, y: data.active_object.data.center.y});
                } else {
                    let step = 50;
                    points = points.concat(data.active_object.points(step));
                }
                for (let point of points) {
                    let pole = new VoronoiPole(scene.objects.layers.special.group, build_data.active_color.value, build_data.active_color.intensity);
                    pole.draw(point.x, point.y);
                    scene.objects.meta_create("special", pole);
                }
                scene.map.voronoi.draw();
                data.active_object.handle.remove();
                data.active_object = null;
                build_data.active_color = null;
                placeholder.end();
                Menu.close("voronoi_tools_area");
            }
        }, build_data);
        build_data.placeholder = voronoi_placeholder;

        this.interaction = null;
        this.interact = () => {
            return new Promise((resolve) => {
                this.interaction = {
                    validate: () => {
                        resolve();
                        this.interaction = null;
                    },
                    cancel: () => {
                        resolve();
                        this.interaction = null;
                    }
                };
                Menu.display(["voronoi_tools_area"]);
            });
        };

        Menu.create({
            name: "voronoi_tools_area", 
            icon: "crowd", 
            contents : ["#voronoi_tools_shape","#voronoi_tools_color"],
            open_handler: () => {
                voronoi_colors.forEach(color => {
                    if (build_data.active_color === null) {
                        return;
                    }
                    if (color.value === build_data.active_color.value) {
                        tools.groups.voronoi_colors.select(`voronoi_color_${color.name}`);
                    }
                });
            },
            close_handler: () => {
                voronoi_placeholder.end();
                tools.groups.voronoi_colors.unselect();
                if (this.interaction !== null) {
                    this.interaction.cancel();
                }
            },
            close_timer: true
        });

        global.tools.register({
            name: "voronoi",
            icon: "crowd f_mtac-icon-xlg",
            position: "#command_graphics_area",
            data: build_data,
            draggable: {
                start: (data, tool) => {
                    let {x: ox, y: oy} = tool.get_container_position();
                    data.origin = {
                        ox: 100 * scene.root_transform.k - ox,
                        oy: 100 * scene.root_transform.k - oy
                    };
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.init({
                        color: global.scene.map.grid.configuration.get("grid.color")
                    });
                    data.placeholder.move_to({x, y});
                },
                drag: (data) => {
                    let {x, y} = scene.transform_coordinates(d3.event, data.origin);
                    data.placeholder.move_to({x, y});
                },
                end: (data, tool) => {
                    if (tool.is_inside(data.placeholder.handle.node())) {
                        data.placeholder.end();
                        return Promise.resolve();
                    }
                    return this.interact();
                }
            }
        });

        tools.register_group({
            name: "voronoi_draw",
            target: "$view",
            enable: (_, selected) => {
                driver_load(selected.name);
            },
            disable: () => {
                driver_unload();
            }
        });

        tools.register({
            group: "voronoi_draw",
            name: "voronoi_line",
            icon: "draw_line",
            position: "#voronoi_tools_shape"
        });
        tools.register({
            group: "voronoi_draw",
            name: "voronoi_disk",
            icon: "draw-circle",
            position: "#voronoi_tools_shape"
        });
        tools.register_group({
            name: "voronoi_colors",
            target: "$none",
            enable: (_, selected) => {
                if (tools.groups.voronoi_draw.selected === null) {
                    tools.groups.voronoi_colors.unselect(selected.name);
                    return;
                }
                color_load(selected.data.color);
            },
            disable: () => {
                color_unload();
            }
        });
        voronoi_colors.forEach(color => {
            tools.register({
                group: "voronoi_colors",
                name: `voronoi_color_${color.name}`,
                data: {color},
                icon: "drop_black",
                position: "#voronoi_tools_color",
                style: {color: color.value, "text-stroke": "1px #fff"}
            });
        });
        // tools.groups.voronoi_draw.select("voronoi_line");

        var selected = null;

        scene.interactions.add_draggable({
            name: "voronoi_pole_move",
            data: {
                active_object: null,
                trace: new Trace(scene.objects.layers.special.group)
            },
            target: {
                layer: scene.objects.layers.special,
                selector: "g.node"
            },
            enable: () => {},
            disable: data => {
                data.active_object = null;
            },
            filter: () =>{
                return d3.event.target.getAttribute("class") !== null && d3.event.target.getAttribute("class").indexOf("object_handle") > -1;
            },
            start: (data, node) => {
                data.active_object = scene.objects.get_by_node("special", node.parentNode);
                data.active_object.top();
                scene.shadow.affect(data.active_object.handle);
                data.trace.init({
                    position: data.active_object.center(),
                    handle: data.active_object.handle,
                    color: scene.map.grid.configuration.get("grid.color")
                });
            },
            update: (data) => {
                data.active_object.move({x: d3.event.dx, y: d3.event.dy});
                data.trace.end(data.active_object.center());
                // enable this for real time draw
                // scene.map.voronoi.reload();
                // scene.map.voronoi.draw();
            },
            end: data => {
                if (data.trace.distance() < 5) {
                    Menu.close("voronoi_update_area");
                    selected = data.active_object;
                    Menu.display(["voronoi_update_area"]);
                    data.active_object = null;
                    data.trace.remove();
                    return;
                }
                scene.shadow.unaffect(data.active_object.handle);
                if (tools.tools["object_remove"].is_inside(d3.event.sourceEvent.target)){
                    scene.objects.tx_delete("special", data.active_object);
                } else {
                    scene.objects.tx_update("special", data.active_object);
                }
                data.active_object = null;
                data.trace.remove();
            }
        });

        scene.interactions.add_to_group({
            group_name: "scene_move",
            interaction_name: "voronoi_pole_move"
        });

        // voronoi editor

        Menu.create({
            name: "voronoi_update_area", 
            icon: "graphics", 
            contents : ["voronoi_colors_update"],
            open_handler : () => {
                if (selected === null) {
                    return;
                }
                let color_intensity = parseInt(selected.node.attr("data-voronoi-intensity"));
                for (let color of voronoi_colors) {
                    if (color.intensity === color_intensity) {
                        tools.group_select("voronoi_colors_update", `voronoi_color_update${color.name}`);
                    }
                }
            },
            close_handler: () => {
                scene.shadow.unaffect(selected.handle);
                selected = null;
            },
            close_timer: true
        });

        tools.register_group({
            name: "voronoi_colors_update",
            target: "$none",
            enable: (_, selected_tool) => {
                let object = selected;
                let color = selected_tool.data.color.value;
                object.node.attr("data-voronoi-intensity", selected_tool.data.color.intensity);
                scene.object_color(object, color);
                scene.objects.tx_update("scene", object);
            },
            disable: () => {}
        });
        voronoi_colors.forEach(color => {
            tools.register({
                group: "voronoi_colors_update",
                name: `voronoi_color_update${color.name}`,
                data: {color},
                icon: "drop_black",
                position: "#voronoi_colors_update",
                style: {color: color.value, "text-stroke": "1px #fff"}
            });
        });
    };

    window.addEventListener("f_mtac.init", () => {
        configuration = global.configuration.module_configuration("voronoi", {});
        if (configuration.get("enabled")) {
            this.init();
        }
    });
};

export default new VoronoiArea();
